import React, { useState, useEffect } from 'react';
import { AdminDesignSystem, ADMIN_ROOT } from './adminDesignSystem';
import AdminSidebar from './AdminSidebar';
import {
  CreditCard,
  Key,
  Mail,
  Save,
  Settings,
  Shield,
  Package
} from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const MainAdminSettings = () => {
  const { user } = useAuth();
  const api = useApi();
  const [activeSection, setActiveSection] = useState('stripe');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activePlanTab, setActivePlanTab] = useState('test'); // 'test' or 'live'
  const [settings, setSettings] = useState({
    stripe: {
      mode: 'test', // 'test' or 'live'
      testKey: 'sk_test_eK4CI3THxJhKfpfYg0sx51Jm00plnjTey3',
      liveKey: '',
      testWebhookSecret: '',
      liveWebhookSecret: '',
      testPriceIds: {
        startup: { monthly: 'price_1OqX8X2eZvKYlo2C9Q9Q9Q9Q', yearly: 'price_1OqX8X2eZvKYlo2C9Q9Q9Q9Q9' },
        growth: { monthly: 'price_1OqX8Y2eZvKYlo2C9Q9Q9Q9Q9', yearly: 'price_1OqX8Y2eZvKYlo2C9Q9Q9Q9Q9Q' },
        enterprise: { monthly: 'price_1OqX8Z2eZvKYlo2C9Q9Q9Q9Q9', yearly: 'price_1OqX8Z2eZvKYlo2C9Q9Q9Q9Q9Q' }
      },
      livePriceIds: {
        startup: { monthly: '', yearly: '' },
        growth: { monthly: '', yearly: '' },
        enterprise: { monthly: '', yearly: '' }
      },
      plans: {
        startup: {
          name: 'Startup',
          price: 3,
          screenshotInterval: 5,
          features: {
            maxUsers: 10,
            maxProjects: 20,
            screenshotRetentionDays: 90,
            customBranding: false,
            prioritySupport: false
          }
        },
        growth: {
          name: 'Growth',
          price: 5,
          screenshotInterval: 3,
          features: {
            maxUsers: 50,
            maxProjects: 100,
            screenshotRetentionDays: 180,
            customBranding: true,
            prioritySupport: true
          }
        },
        enterprise: {
          name: 'Enterprise',
          price: 7,
          screenshotInterval: 1,
          features: {
            maxUsers: 1000,
            maxProjects: 500,
            screenshotRetentionDays: 365,
            customBranding: true,
            prioritySupport: true,
            sso: true,
            apiAccess: true
          }
        }
      }
    },
    system: {
      maintenanceMode: false,
      registrationEnabled: true,
      emailVerificationRequired: true,
      maxUsersPerOrg: 100,
      maxProjectsPerOrg: 50,
      screenshotRetentionDays: 90,
      aiAnalysisEnabled: true
    },
    email: {
      smtpHost: '',
      smtpPort: 587,
      smtpUser: '',
      smtpPass: '',
      fromEmail: 'noreply@teamgrid.com',
      fromName: 'TeamGrid'
    },
    security: {
      sessionTimeout: 24, // hours
      maxLoginAttempts: 5,
      lockoutDuration: 30, // minutes
      requireMfa: false,
      passwordMinLength: 8
    },
    plans: {
      startup: {
        name: 'Startup',
        price: 3,
        screenshotInterval: 5,
        features: {
          maxUsers: 10,
          maxProjects: 20,
          screenshotRetentionDays: 90,
          customBranding: false,
          prioritySupport: false
        }
      },
      growth: {
        name: 'Growth',
        price: 5,
        screenshotInterval: 3,
        features: {
          maxUsers: 50,
          maxProjects: 100,
          screenshotRetentionDays: 180,
          customBranding: true,
          prioritySupport: true
        }
      },
      enterprise: {
        name: 'Enterprise',
        price: 7,
        screenshotInterval: 1,
        features: {
          maxUsers: 1000,
          maxProjects: 500,
          screenshotRetentionDays: 365,
          customBranding: true,
          prioritySupport: true,
          sso: true,
          apiAccess: true
        }
      }
    }
  });

  const navigation = [
    {
      id: 'stripe',
      label: 'Stripe Configuration',
      icon: CreditCard,
      description: 'Manage payment processing settings'
    },
    {
      id: 'system',
      label: 'System Settings',
      icon: Settings,
      description: 'Core application configuration'
    },
    {
      id: 'email',
      label: 'Email Configuration',
      icon: Mail,
      description: 'SMTP and email settings'
    },
    {
      id: 'security',
      label: 'Security Settings',
      icon: Shield,
      description: 'Authentication and security policies'
    }
  ];



  useEffect(() => {
    if (user?.role === 'main_admin') {
      loadSettings();
    }
  }, [user]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/v1/admin/settings');
      const apiSettings = response.data.data;
      
      // Ensure proper structure for plans and price IDs
      const settingsWithPlans = {
        ...apiSettings,
        stripe: {
          ...apiSettings.stripe,
          testPriceIds: {
            startup: { 
              monthly: apiSettings.stripe?.testPriceIds?.startup?.monthly || 'price_1OqX8X2eZvKYlo2C9Q9Q9Q9Q', 
              yearly: apiSettings.stripe?.testPriceIds?.startup?.yearly || 'price_1OqX8X2eZvKYlo2C9Q9Q9Q9Q9' 
            },
            growth: { 
              monthly: apiSettings.stripe?.testPriceIds?.growth?.monthly || 'price_1OqX8Y2eZvKYlo2C9Q9Q9Q9Q9', 
              yearly: apiSettings.stripe?.testPriceIds?.growth?.yearly || 'price_1OqX8Y2eZvKYlo2C9Q9Q9Q9Q9Q' 
            },
            enterprise: { 
              monthly: apiSettings.stripe?.testPriceIds?.enterprise?.monthly || 'price_1OqX8Z2eZvKYlo2C9Q9Q9Q9Q9', 
              yearly: apiSettings.stripe?.testPriceIds?.enterprise?.yearly || 'price_1OqX8Z2eZvKYlo2C9Q9Q9Q9Q9Q' 
            }
          },
          livePriceIds: {
            startup: { 
              monthly: apiSettings.stripe?.livePriceIds?.startup?.monthly || '', 
              yearly: apiSettings.stripe?.livePriceIds?.startup?.yearly || '' 
            },
            growth: { 
              monthly: apiSettings.stripe?.livePriceIds?.growth?.monthly || '', 
              yearly: apiSettings.stripe?.livePriceIds?.growth?.yearly || '' 
            },
            enterprise: { 
              monthly: apiSettings.stripe?.livePriceIds?.enterprise?.monthly || '', 
              yearly: apiSettings.stripe?.livePriceIds?.enterprise?.yearly || '' 
            }
          },
          plans: apiSettings.plans || {
            startup: {
              name: 'Startup',
              price: 3,
              screenshotInterval: 5,
              features: {
                maxUsers: 10,
                maxProjects: 20,
                screenshotRetentionDays: 90,
                customBranding: false,
                prioritySupport: false
              }
            },
            growth: {
              name: 'Growth',
              price: 5,
              screenshotInterval: 3,
              features: {
                maxUsers: 50,
                maxProjects: 100,
                screenshotRetentionDays: 180,
                customBranding: true,
                prioritySupport: true
              }
            },
            enterprise: {
              name: 'Enterprise',
              price: 7,
              screenshotInterval: 1,
              features: {
                maxUsers: 1000,
                maxProjects: 500,
                screenshotRetentionDays: 365,
                customBranding: true,
                prioritySupport: true,
                sso: true,
                apiAccess: true
              }
            }
          }
        }
      };
      
      setSettings(settingsWithPlans);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (section) => {
    try {
      setSaving(true);
      
      const response = await api.put(`/api/v1/admin/settings/${section}`, {
        [section]: settings[section]
      });
      
      if (response.data.success) {
        toast.success(`${navigation.find(n => n.id === section)?.label} updated successfully`);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const savePlans = async () => {
    try {
      setSaving(true);
      
      const response = await api.put(`/api/v1/admin/settings/plans`, {
        plans: settings.stripe.plans
      });
      
      if (response.data.success) {
        toast.success('Plan settings updated successfully');
      }
    } catch (error) {
      console.error('Error saving plans:', error);
      toast.error('Failed to save plan settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleStripeMode = () => {
    setSettings(prev => ({
      ...prev,
      stripe: {
        ...prev.stripe,
        mode: prev.stripe.mode === 'test' ? 'live' : 'test'
      }
    }));
  };

  const updateStripeSetting = (field, value) => {
    setSettings(prev => ({
      ...prev,
      stripe: {
        ...prev.stripe,
        [field]: value
      }
    }));
  };

  const updateSystemSetting = (field, value) => {
    setSettings(prev => ({
      ...prev,
      system: {
        ...prev.system,
        [field]: value
      }
    }));
  };

  const updateEmailSetting = (field, value) => {
    setSettings(prev => ({
      ...prev,
      email: {
        ...prev.email,
        [field]: value
      }
    }));
  };

  const updateSecuritySetting = (field, value) => {
    setSettings(prev => ({
      ...prev,
      security: {
        ...prev.security,
        [field]: value
      }
    }));
  };

  const updatePlansSetting = (plan, field, value) => {
    setSettings(prev => ({
      ...prev,
      stripe: {
        ...prev.stripe,
        plans: {
          ...prev.stripe.plans,
          [plan]: {
            ...prev.stripe.plans[plan],
            [field]: value
          }
        }
      }
    }));
  };

  const updatePlanFeature = (plan, feature, value) => {
    setSettings(prev => ({
      ...prev,
      stripe: {
        ...prev.stripe,
        plans: {
          ...prev.stripe.plans,
          [plan]: {
            ...prev.stripe.plans[plan],
            features: {
              ...prev.stripe.plans[plan].features,
              [feature]: value
            }
          }
        }
      }
    }));
  };

  const renderStripeSettings = () => {
    if (loading || !settings.stripe) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-gray-400">Loading Stripe configuration...</p>
        </div>
      );
    }

    return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-white">Payment Processing</h3>
            <p className="text-sm text-gray-400">Configure Stripe payment processing and plans</p>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-sm text-gray-400">Test Mode</span>
          <button
            onClick={toggleStripeMode}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              settings.stripe.mode === 'live' ? 'bg-blue-600' : 'bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.stripe.mode === 'live' ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <span className="text-sm text-gray-400">Live Mode</span>
        </div>
      </div>

        {/* Stripe Credentials */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Test Configuration */}
        <div className="bg-white/5 border border-white/10 rounded-lg p-6">
          <h4 className="text-md font-medium text-white mb-4 flex items-center">
            <Key className="h-4 w-4 mr-2" />
            Test Configuration
          </h4>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Test Secret Key
              </label>
              <input
                type="password"
                value={settings.stripe.testKey}
                onChange={(e) => updateStripeSetting('testKey', e.target.value)}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/20"
                placeholder="sk_test_..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Test Webhook Secret
              </label>
              <input
                type="password"
                value={settings.stripe.testWebhookSecret}
                onChange={(e) => updateStripeSetting('testWebhookSecret', e.target.value)}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/20"
                placeholder="whsec_..."
              />
            </div>
          </div>
        </div>

        {/* Live Configuration */}
        <div className="bg-white/5 border border-white/10 rounded-lg p-6">
          <h4 className="text-md font-medium text-white mb-4 flex items-center">
            <Shield className="h-4 w-4 mr-2" />
            Live Configuration
          </h4>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Live Secret Key
              </label>
              <input
                type="password"
                value={settings.stripe.liveKey}
                onChange={(e) => updateStripeSetting('liveKey', e.target.value)}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/20"
                placeholder="sk_live_..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Live Webhook Secret
              </label>
              <input
                type="password"
                value={settings.stripe.liveWebhookSecret}
                onChange={(e) => updateStripeSetting('liveWebhookSecret', e.target.value)}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/20"
                placeholder="whsec_..."
              />
            </div>
          </div>
        </div>
      </div>

        {/* Plan Management */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-6">
          <h4 className="text-md font-medium text-white mb-4">Plan Management</h4>
          
          {/* Plan Tabs */}
          <div className="flex space-x-1 mb-6">
            <button
              onClick={() => setActivePlanTab('test')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                activePlanTab === 'test'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'
              }`}
            >
              Test Plans
            </button>
            <button
              onClick={() => setActivePlanTab('live')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                activePlanTab === 'live'
                  ? 'bg-green-600 text-white'
                  : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'
              }`}
            >
              Live Plans
            </button>
          </div>

          {/* Plan Configuration */}
          <div className="space-y-8">
            {!settings.stripe?.plans ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
                <p className="text-gray-400">Loading plan configuration...</p>
              </div>
            ) : (
              ['startup', 'growth', 'enterprise'].map((plan) => (
              <div key={plan} className="bg-white/5 border border-white/10 rounded-lg p-6">
                <h5 className="text-lg font-medium text-white mb-4 flex items-center capitalize">
                  <Package className="h-5 w-5 mr-2" />
                  {plan} Plan
                </h5>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Plan Configuration */}
                  <div className="space-y-4">
              <div>
                      <label className="block text-sm font-medium text-white mb-1">Plan Name</label>
                <input
                  type="text"
                        value={settings.stripe.plans[plan].name}
                        onChange={(e) => updatePlansSetting(plan, 'name', e.target.value)}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-white/20"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-1">Price ($/month)</label>
                      <input
                        type="number"
                        value={settings.stripe.plans[plan].price}
                        onChange={(e) => updatePlansSetting(plan, 'price', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-white/20"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-1">Screenshots Every (minutes)</label>
                      <input
                        type="number"
                        min="1"
                        max="60"
                        value={settings.stripe.plans[plan].screenshotInterval}
                        onChange={(e) => updatePlansSetting(plan, 'screenshotInterval', parseInt(e.target.value))}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-white/20"
                      />
                      <p className="text-xs text-gray-400 mt-1">Enter 1, 2, 3, 4, or any number of minutes</p>
                    </div>

                    {/* Stripe Plan IDs */}
                    <div>
                      <label className="block text-sm font-medium text-white mb-1">
                        {activePlanTab === 'test' ? 'Test' : 'Live'} Monthly Price ID
                      </label>
                      <input
                        type="text"
                        value={settings.stripe?.[`${activePlanTab}PriceIds`]?.[plan]?.monthly || ''}
                        onChange={(e) => updateStripeSetting(`${activePlanTab}PriceIds`, {
                          ...settings.stripe[`${activePlanTab}PriceIds`],
                          [plan]: { ...settings.stripe[`${activePlanTab}PriceIds`][plan], monthly: e.target.value }
                  })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/20 text-sm"
                  placeholder="price_..."
                />
              </div>
              
              <div>
                      <label className="block text-sm font-medium text-white mb-1">
                        {activePlanTab === 'test' ? 'Test' : 'Live'} Yearly Price ID
                      </label>
                <input
                  type="text"
                        value={settings.stripe?.[`${activePlanTab}PriceIds`]?.[plan]?.yearly || ''}
                        onChange={(e) => updateStripeSetting(`${activePlanTab}PriceIds`, {
                          ...settings.stripe[`${activePlanTab}PriceIds`],
                          [plan]: { ...settings.stripe[`${activePlanTab}PriceIds`][plan], yearly: e.target.value }
                  })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/20 text-sm"
                  placeholder="price_..."
                />
              </div>
            </div>

                  {/* Plan Features */}
                  <div className="space-y-4">
                    <h6 className="text-sm font-medium text-white">Features</h6>
                    
                    <div>
                      <label className="block text-sm font-medium text-white mb-1">Max Users</label>
                      <input
                        type="number"
                        value={settings.stripe.plans[plan].features.maxUsers}
                        onChange={(e) => updatePlanFeature(plan, 'maxUsers', parseInt(e.target.value))}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-white/20"
                      />
        </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-1">Max Projects</label>
                      <input
                        type="number"
                        value={settings.stripe.plans[plan].features.maxProjects}
                        onChange={(e) => updatePlanFeature(plan, 'maxProjects', parseInt(e.target.value))}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-white/20"
                      />
      </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-1">Screenshot Retention (days)</label>
                      <input
                        type="number"
                        value={settings.stripe.plans[plan].features.screenshotRetentionDays}
                        onChange={(e) => updatePlanFeature(plan, 'screenshotRetentionDays', parseInt(e.target.value))}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-white/20"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-white">Custom Branding</label>
                        <p className="text-xs text-gray-400">Allow custom branding</p>
                      </div>
                      <button
                        onClick={() => updatePlanFeature(plan, 'customBranding', !settings.stripe.plans[plan].features.customBranding)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          settings.stripe.plans[plan].features.customBranding ? 'bg-green-600' : 'bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            settings.stripe.plans[plan].features.customBranding ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-white">Priority Support</label>
                        <p className="text-xs text-gray-400">Priority customer support</p>
                      </div>
                      <button
                        onClick={() => updatePlanFeature(plan, 'prioritySupport', !settings.stripe.plans[plan].features.prioritySupport)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          settings.stripe.plans[plan].features.prioritySupport ? 'bg-green-600' : 'bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            settings.stripe.plans[plan].features.prioritySupport ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Enterprise-only features */}
                    {plan === 'enterprise' && (
                      <>
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-white">SSO</label>
                            <p className="text-xs text-gray-400">Single Sign-On</p>
                          </div>
                          <button
                            onClick={() => updatePlanFeature(plan, 'sso', !settings.stripe.plans[plan].features.sso)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              settings.stripe.plans[plan].features.sso ? 'bg-green-600' : 'bg-gray-600'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                settings.stripe.plans[plan].features.sso ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-white">API Access</label>
                            <p className="text-xs text-gray-400">API integration access</p>
                          </div>
                          <button
                            onClick={() => updatePlanFeature(plan, 'apiAccess', !settings.stripe.plans[plan].features.apiAccess)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              settings.stripe.plans[plan].features.apiAccess ? 'bg-green-600' : 'bg-gray-600'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                settings.stripe.plans[plan].features.apiAccess ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={() => savePlans()}
            disabled={saving}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Plans'}
          </button>
        <button
          onClick={() => saveSettings('stripe')}
          disabled={saving}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Stripe Settings'}
        </button>
      </div>
    </div>
  );
  };

  const renderSystemSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-white">System Configuration</h3>
        <p className="text-sm text-gray-400">Core application settings and limits</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-white">Maintenance Mode</label>
              <p className="text-xs text-gray-400">Temporarily disable user access</p>
            </div>
            <button
              onClick={() => updateSystemSetting('maintenanceMode', !settings.system.maintenanceMode)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.system.maintenanceMode ? 'bg-red-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.system.maintenanceMode ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-white">Registration Enabled</label>
              <p className="text-xs text-gray-400">Allow new user registrations</p>
            </div>
            <button
              onClick={() => updateSystemSetting('registrationEnabled', !settings.system.registrationEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.system.registrationEnabled ? 'bg-green-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.system.registrationEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-white">Email Verification Required</label>
              <p className="text-xs text-gray-400">Require email verification for new users</p>
            </div>
            <button
              onClick={() => updateSystemSetting('emailVerificationRequired', !settings.system.emailVerificationRequired)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.system.emailVerificationRequired ? 'bg-green-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.system.emailVerificationRequired ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-white">AI Analysis Enabled</label>
              <p className="text-xs text-gray-400">Enable AI-powered activity analysis</p>
            </div>
            <button
              onClick={() => updateSystemSetting('aiAnalysisEnabled', !settings.system.aiAnalysisEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.system.aiAnalysisEnabled ? 'bg-green-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.system.aiAnalysisEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white mb-1">
              Max Users Per Organization
            </label>
            <input
              type="number"
              value={settings.system.maxUsersPerOrg}
              onChange={(e) => updateSystemSetting('maxUsersPerOrg', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-white/20"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-1">
              Max Projects Per Organization
            </label>
            <input
              type="number"
              value={settings.system.maxProjectsPerOrg}
              onChange={(e) => updateSystemSetting('maxProjectsPerOrg', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-white/20"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-1">
              Screenshot Retention (Days)
            </label>
            <input
              type="number"
              value={settings.system.screenshotRetentionDays}
              onChange={(e) => updateSystemSetting('screenshotRetentionDays', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-white/20"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => saveSettings('system')}
          disabled={saving}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save System Settings'}
        </button>
      </div>
    </div>
  );

  const renderEmailSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-white">Email Configuration</h3>
        <p className="text-sm text-gray-400">SMTP settings for sending emails</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white mb-1">SMTP Host</label>
            <input
              type="text"
              value={settings.email.smtpHost}
              onChange={(e) => updateEmailSetting('smtpHost', e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/20"
              placeholder="smtp.gmail.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-1">SMTP Port</label>
            <input
              type="number"
              value={settings.email.smtpPort}
              onChange={(e) => updateEmailSetting('smtpPort', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-white/20"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-1">SMTP Username</label>
            <input
              type="text"
              value={settings.email.smtpUser}
              onChange={(e) => updateEmailSetting('smtpUser', e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/20"
              placeholder="your-email@gmail.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-1">SMTP Password</label>
            <input
              type="password"
              value={settings.email.smtpPass}
              onChange={(e) => updateEmailSetting('smtpPass', e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/20"
              placeholder="App password"
            />
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white mb-1">From Email</label>
            <input
              type="email"
              value={settings.email.fromEmail}
              onChange={(e) => updateEmailSetting('fromEmail', e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/20"
              placeholder="noreply@teamgrid.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-1">From Name</label>
            <input
              type="text"
              value={settings.email.fromName}
              onChange={(e) => updateEmailSetting('fromName', e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/20"
              placeholder="TeamGrid"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => saveSettings('email')}
          disabled={saving}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Email Settings'}
        </button>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-white">Security Configuration</h3>
        <p className="text-sm text-gray-400">Authentication and security policies</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white mb-1">Session Timeout (Hours)</label>
            <input
              type="number"
              value={settings.security.sessionTimeout}
              onChange={(e) => updateSecuritySetting('sessionTimeout', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-white/20"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-1">Max Login Attempts</label>
            <input
              type="number"
              value={settings.security.maxLoginAttempts}
              onChange={(e) => updateSecuritySetting('maxLoginAttempts', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-white/20"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-1">Lockout Duration (Minutes)</label>
            <input
              type="number"
              value={settings.security.lockoutDuration}
              onChange={(e) => updateSecuritySetting('lockoutDuration', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-white/20"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-1">Password Min Length</label>
            <input
              type="number"
              value={settings.security.passwordMinLength}
              onChange={(e) => updateSecuritySetting('passwordMinLength', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-white/20"
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-white">Require MFA</label>
              <p className="text-xs text-gray-400">Enable multi-factor authentication</p>
            </div>
            <button
              onClick={() => updateSecuritySetting('requireMfa', !settings.security.requireMfa)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.security.requireMfa ? 'bg-green-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.security.requireMfa ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => saveSettings('security')}
          disabled={saving}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Security Settings'}
        </button>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'stripe':
        return renderStripeSettings();
      case 'system':
        return renderSystemSettings();
      case 'email':
        return renderEmailSettings();
      case 'security':
        return renderSecuritySettings();
      default:
        return renderStripeSettings();
    }
  };

  if (user?.role !== 'main_admin') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-gray-400">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className={`${ADMIN_ROOT} h-screen flex overflow-hidden bg-black`}>
      <AdminDesignSystem />
      <AdminSidebar activeKey="settings" />

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        <div className="flex-1 relative overflow-y-auto focus:outline-none">
      <div className="w-full px-5 sm:px-6 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white mb-1">System Settings</h1>
          <p className="text-sm text-gray-400">Manage application configuration and settings</p>
        </div>



        <div className="flex flex-col lg:flex-row gap-6">
              {/* Settings Sidebar Navigation */}
              <div className="lg:w-64 w-full">
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-white mb-3 border-b border-white/10 pb-2">Settings Navigation</h3>
              <nav className="space-y-2">
                    {navigation && navigation.length > 0 ? (
                      navigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveSection(item.id)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-sm transition-colors ${
                        activeSection === item.id
                          ? 'bg-white/10 text-white'
                          : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{item.label}</span>
                    </button>
                  );
                      })
                    ) : (
                      <div className="text-red-400 text-sm">No navigation items found!</div>
                    )}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-white/5 border border-white/10 rounded-lg p-6">
              {renderContent()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainAdminSettings;
